import { useRef, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import { t } from '../i18n/index.js';

/**
 * ADR 0132 (Gestaltungsvertrag): gemeinsame Bausteine für Werkzeugkopf, Ansichtswechsel, Filter und Abschnitte.
 * Abstände nur aus der Skala 4/8/12/16/24/32 px (Tailwind 1/2/3/4/6/8). Gleiche Gestaltung, aber passende Bedienung:
 * echte Routen als Links (`semantics="nav"`), lokale Panels als vollständige Reitersteuerung (`semantics="tabs"`).
 */
const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');

export type ViewItem<K extends string> = { id: K; label: string; icon?: ReactNode; badge?: number; badgeLabel?: string; href?: string; /** kleiner Hinweispunkt mit gesprochenem Text (z. B. „Filter aktiv“) */ marker?: string };

/**
 * Ansichtswechsel mit Icon und Text, gleicher Höhe, Aktivmarkierung (Fläche + Unterstrich, nicht nur Farbe) und Fokus.
 * Ab drei Ansichten erscheint auf schmalen Flächen eine zugängliche Auswahl statt einer überquellenden Knopfreihe.
 */
export function ViewSwitch<K extends string>({ label, items, value, onChange, semantics = 'tabs', panelId, className, ...data }: { label: string; items: ViewItem<K>[]; value: K; onChange: (id: K) => void; semantics?: 'tabs' | 'nav'; panelId?: string; className?: string } & { [k: `data-${string}`]: string }) {
  const refs = useRef<(HTMLElement | null)[]>([]);
  const collapsible = items.length >= 3;
  const move = (e: KeyboardEvent, i: number) => {
    if (semantics !== 'tabs') return;
    const n = items.length;
    const to = e.key === 'ArrowRight' ? (i + 1) % n : e.key === 'ArrowLeft' ? (i - 1 + n) % n : e.key === 'Home' ? 0 : e.key === 'End' ? n - 1 : -1;
    if (to < 0) return;
    e.preventDefault();
    onChange(items[to]!.id);
    refs.current[to]?.focus();
  };
  const inner = (it: ViewItem<K>) => (
    <>
      {it.icon}
      <span>{it.label}</span>
      {it.marker && (
        <>
          <span aria-hidden className="size-2 rounded-full bg-[var(--color-accent)]" data-view-marker="" />
          <span className="sr-only">({it.marker})</span>
        </>
      )}
      {!!it.badge && (
        <span className="rounded-full bg-[var(--color-accent)] px-2 text-xs font-medium text-on-accent" aria-label={it.badgeLabel}>
          {it.badge > 99 ? '99+' : it.badge}
        </span>
      )}
    </>
  );
  const cls = 'view-switch-item';
  return (
    <div className={cx('view-switch', collapsible && 'view-switch-collapsible', className)} {...data}>
      {semantics === 'tabs' ? (
        <div role="tablist" aria-label={label} className="view-switch-row">
          {items.map((it, i) => (
            <button
              key={it.id}
              ref={(el) => void (refs.current[i] = el)}
              type="button"
              role="tab"
              id={panelId ? `${panelId}-tab-${it.id}` : undefined}
              aria-controls={panelId}
              aria-selected={value === it.id}
              tabIndex={value === it.id ? 0 : -1}
              onClick={() => onChange(it.id)}
              onKeyDown={(e) => move(e, i)}
              className={cls}
              data-view={it.id}
            >
              {inner(it)}
            </button>
          ))}
        </div>
      ) : (
        <nav aria-label={label} className="view-switch-row">
          {items.map((it) =>
            it.href ? (
              <a
                key={it.id}
                href={it.href}
                aria-current={value === it.id ? 'page' : undefined}
                onClick={(e: MouseEvent) => {
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                  e.preventDefault();
                  onChange(it.id);
                }}
                className={cls}
                data-view={it.id}
              >
                {inner(it)}
              </a>
            ) : (
              <button key={it.id} type="button" aria-current={value === it.id ? 'page' : undefined} onClick={() => onChange(it.id)} className={cls} data-view={it.id}>
                {inner(it)}
              </button>
            ),
          )}
        </nav>
      )}
      {collapsible && (
        <select className="view-switch-select min-h-11 rounded-lg border border-zinc-300 bg-white px-3 text-sm dark:border-zinc-700 dark:bg-zinc-900" aria-label={label} value={value} onChange={(e) => onChange(e.target.value as K)}>
          {items.map((it) => (
            <option key={it.id} value={it.id}>
              {it.label}
              {it.marker ? ` (${it.marker})` : ''}
              {it.badge ? ` (${it.badge})` : ''}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

/**
 * Werkzeugkopf: links Titel/Pfad und Ansichten, daneben Filter, rechts sekundäre und die eine Hauptaktion.
 * Dezenter Kopf mit Trennlinie (`section-toolbar`); bricht auf schmalen Flächen geordnet um.
 */
export function ToolHeader({ title, views, filters, secondary, primary, children, className, ...data }: { title?: ReactNode; views?: ReactNode; filters?: ReactNode; secondary?: ReactNode; primary?: ReactNode; children?: ReactNode; className?: string } & { [k: `data-${string}`]: string }) {
  return (
    <div className={cx('section-toolbar tool-header', className)} {...data}>
      {title && <div className="tool-header-title min-w-0 text-base font-semibold">{title}</div>}
      {views}
      {filters && <div className="tool-header-filters flex min-w-0 flex-wrap items-center gap-2">{filters}</div>}
      <span className="hidden flex-1 sm:block" />
      {(secondary || primary) && (
        <div className="tool-header-actions flex flex-wrap items-center gap-2">
          {secondary}
          {primary}
        </div>
      )}
      {children}
    </div>
  );
}

/** Filterchip: als Filter erkennbar (Rahmen, Häkchen-Zustand über aria-pressed), nicht mit Navigation verwechselbar */
export function FilterChip({ pressed, onToggle, children, count, ...data }: { pressed: boolean; onToggle: () => void; children: ReactNode; count?: number } & { [k: `data-${string}`]: string }) {
  return (
    <button type="button" aria-pressed={pressed} onClick={onToggle} className="filter-chip" {...data}>
      {pressed && (
        <svg aria-hidden viewBox="0 0 16 16" className="size-3">
          <path d="M3 8.5l3 3 7-7" fill="none" stroke="currentColor" strokeWidth="2" />
        </svg>
      )}
      <span>{children}</span>
      {count !== undefined && <span className="tabular-nums text-zinc-500 dark:text-zinc-400">{count}</span>}
    </button>
  );
}

/** Filtergruppe mit einheitlichem Zustand: Anzahl aktiver Filter, Zurücksetzen und Ergebnis (ansagbar) */
export function FilterBar({ label, active, onReset, result, children }: { label: string; active: number; onReset?: () => void; result?: string; children: ReactNode }) {
  return (
    <div role="group" aria-label={label} className="flex min-w-0 flex-wrap items-center gap-2" data-filter-bar="">
      {children}
      {active > 0 && onReset && (
        <button type="button" onClick={onReset} className="min-h-10 rounded-lg px-2 text-sm text-zinc-600 underline-offset-2 hover:underline dark:text-zinc-400" data-filter-reset="">
          {t('filter.reset', { n: active })}
        </button>
      )}
      {result && (
        <span className="text-sm text-zinc-500" aria-live="polite" data-filter-result="">
          {result}
        </span>
      )}
    </div>
  );
}

/** Abschnitt mit festem Kopf (Titel, Anzahl, Aktionen); Hauptabschnitte stehen 24–32 px auseinander */
export function Section({ title, count, actions, hint, children, level = 2, className, ...data }: { title: ReactNode; count?: number; actions?: ReactNode; hint?: ReactNode; children: ReactNode; level?: 2 | 3; className?: string } & { [k: `data-${string}`]: string }) {
  const H = level === 2 ? 'h2' : 'h3';
  return (
    <section className={cx('tool-section', className)} {...data}>
      <div className="tool-section-head">
        <H className={level === 2 ? 'text-base font-semibold' : 'text-sm font-semibold'}>
          {title}
          {count !== undefined && <span className="ml-2 font-normal text-zinc-500 tabular-nums">{count}</span>}
        </H>
        <span className="flex-1" />
        {actions}
      </div>
      {hint && <p className="text-sm text-zinc-600 dark:text-zinc-400">{hint}</p>}
      {children}
    </section>
  );
}

/** Leerer Zustand: ruhig, ohne große tote Fläche; höchstens ein nächster Schritt */
export function EmptyState({ children, action, ...data }: { children: ReactNode; action?: ReactNode } & { [k: `data-${string}`]: string }) {
  return (
    <div className="rounded-xl border border-dashed border-zinc-300 p-4 text-center text-sm text-zinc-600 dark:border-zinc-700 dark:text-zinc-400" {...data}>
      <p>{children}</p>
      {action && <div className="mt-3 flex justify-center">{action}</div>}
    </div>
  );
}

/** Legende: Zeichen + Wort (verständlich ohne Farbe); ruhig unter dem Werkzeugkopf */
export function Legend({ label, items }: { label: string; items: { mark: ReactNode; text: string }[] }) {
  return (
    <ul aria-label={label} className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-600 dark:text-zinc-400" data-legend="">
      {items.map((x) => (
        <li key={x.text} className="inline-flex items-center gap-1">
          <span aria-hidden className="inline-flex items-center">
            {x.mark}
          </span>
          {x.text}
        </li>
      ))}
    </ul>
  );
}
